import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { monthlyAssessmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import { patientAPI } from '../services/api';
import { tokenManager } from '../services/tokenManager';

// Local storage key generator
const getLocalStorageKey = (sessionId) => `monthly_assessment_${sessionId}`;

// Load saved assessment data from local storage
const loadSavedAssessment = (sessionId) => {
  try {
    const saved = localStorage.getItem(getLocalStorageKey(sessionId));
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error loading saved assessment:', error);
    return null;
  }
};

// Save assessment data to local storage
const saveAssessment = (sessionId, data) => {
  try {
    localStorage.setItem(getLocalStorageKey(sessionId), JSON.stringify(data));
  } catch (error) {
    console.error('Error saving assessment:', error);
  }
};

// Clear saved assessment data
const clearSavedAssessment = (sessionId) => {
  try {
    localStorage.removeItem(getLocalStorageKey(sessionId));
  } catch (error) {
    console.error('Error clearing saved assessment:', error);
  }
};

const MonthlyAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [patientProfile, setPatientProfile] = useState(null);
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [sessionState, setSessionState] = useState(null);

  // Get sessionId from URL
  const sessionId = location.pathname.split('/').pop();

  useEffect(() => {
    // Get state from URL parameters
    const params = new URLSearchParams(location.search);
    const stateParam = params.get('state');
    
    if (stateParam) {
      try {
        const stateData = JSON.parse(stateParam);
        setSessionState(stateData);
      } catch (error) {
        console.error('Error parsing state data:', error);
      }
    }

    // Validate session ID format
    if (!sessionId || !/^[0-9a-fA-F]{24}$/.test(sessionId)) {
      toast.error('Invalid session ID format. Please check your link.');
      setLoading(false);
      return;
    }

    const initializeAssessment = async () => {
      try {
        // Try to load saved assessment first
        const savedAssessment = loadSavedAssessment(sessionId);
        
        if (savedAssessment) {
          setFormData(savedAssessment);
        } else {
          // Try to get existing assessment from API
          const response = await monthlyAssessmentAPI.getMonthlyAssessmentBySessionId(sessionId);
          if (response?.assessment) {
            setExistingAssessment(response.assessment);
            // Transform API data to form format
            const transformedData = transformAPIDataToFormData(response.assessment);
            setFormData(transformedData);
            // Save to local storage for future use
            saveAssessment(sessionId, transformedData);
          }
        }

        // Get patient profile if user is logged in
        const token = tokenManager.getAccessToken();
        if (token) {
          const response = await patientAPI.getMyProfile();
          setPatientProfile(response.patientProfile);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // No existing assessment found - this is fine, use default form data
          console.log('No existing assessment found for session:', sessionId);
        } else {
          toast.error('Error loading assessment data. Starting with empty form.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAssessment();
  }, [sessionId, location.search]);

  const [formData, setFormData] = useState({
    // Step 1: Core Health Metrics
    coreHealthMetrics: {
      adhdSymptoms: 50,
      mood: 50,
      focus: 50,
      sleepQuality: 50,
      appetite: 50,
      productivity: 50,
      energy: 50,
      stress: 50
    },

    // Step 2: Medication Review
    medicationReview: {
      takingAsPrescibed: '',
      missedDoses: 0,
      sideEffectsSeverity: 50,
      sideEffectsDescription: '',
      medicationEffectiveness: 50
    },

    // Step 3: Life Impact
    lifeImpact: {
      workImpact: 50,
      relationships: 50,
      dailyActivities: 50
    },

    // Step 4: Additional Information
    additionalInfo: {
      biggestChallenges: '',
      helpfulStrategies: '',
      goals: '',
      questions: ''
    }
  });

  // Transform API data to form data format
  const transformAPIDataToFormData = (apiData) => {
    return {
      coreHealthMetrics: {
        adhdSymptoms: apiData.symptomManagement * 10 || 50,
        mood: apiData.mood * 10 || 50,
        focus: apiData.focus * 10 || 50,
        sleepQuality: apiData.sleep * 10 || 50,
        appetite: apiData.appetite * 10 || 50,
        productivity: apiData.productivity * 10 || 50,
        energy: apiData.energy * 10 || 50,
        stress: apiData.stress * 10 || 50
      },
      medicationReview: {
        takingAsPrescibed: apiData.medicationAdherence === 'yes' ? 'Yes' : 
                          apiData.medicationAdherence === 'sometimes' ? 'Sometimes' : 
                          apiData.medicationAdherence === 'no' ? 'No' : '',
        missedDoses: apiData.missedDosesPerWeek || 0,
        sideEffectsSeverity: apiData.sideEffectsSeverity * 10 || 50,
        sideEffectsDescription: apiData.sideEffectsDescription || '',
        medicationEffectiveness: apiData.medicationEffectiveness * 10 || 50
      },
      lifeImpact: {
        workImpact: apiData.workPerformance * 10 || 50,
        relationships: apiData.relationshipImpact * 10 || 50,
        dailyActivities: apiData.dailyActivities * 10 || 50
      },
      additionalInfo: {
        biggestChallenges: apiData.biggestChallenges || '',
        helpfulStrategies: apiData.whatHelpsMost || '',
        goals: apiData.goalsNextMonth || '',
        questions: apiData.questionsForNurse || ''
      }
    };
  };

  const steps = [
    { label: "Health Metrics", key: "coreHealthMetrics" },
    { label: "Medication Review", key: "medicationReview" },
    { label: "Life Impact", key: "lifeImpact" },
    { label: "Additional Information", key: "additionalInfo" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');
    
    const newFormData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value
      }
    };
    
    setFormData(newFormData);
    // Auto-save to local storage
    saveAssessment(sessionId, newFormData);
  };

  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    const [section, field] = name.split('.');
    
    const newFormData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: parseInt(value)
      }
    };
    
    setFormData(newFormData);
    // Auto-save to local storage
    saveAssessment(sessionId, newFormData);
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = (e) => {
    if (e) e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep !== steps.length) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Transform form data to match API schema
      const assessmentData = {
        sessionId: sessionId,
        patientId: patientProfile?._id,
        symptomManagement: formData.coreHealthMetrics.adhdSymptoms / 10,
        mood: formData.coreHealthMetrics.mood / 10,
        focus: formData.coreHealthMetrics.focus / 10,
        sleep: formData.coreHealthMetrics.sleepQuality / 10,
        appetite: formData.coreHealthMetrics.appetite / 10,
        productivity: formData.coreHealthMetrics.productivity / 10,
        energy: formData.coreHealthMetrics.energy / 10,
        stress: formData.coreHealthMetrics.stress / 10,
        medicationAdherence: formData.medicationReview.takingAsPrescibed === 'Yes' ? 'yes' : 
                           formData.medicationReview.takingAsPrescibed === 'Sometimes' ? 'sometimes' : 'no',
        missedDosesPerWeek: parseInt(formData.medicationReview.missedDoses),
        sideEffectsSeverity: formData.medicationReview.sideEffectsSeverity / 10,
        sideEffectsDescription: formData.medicationReview.sideEffectsDescription,
        medicationEffectiveness: formData.medicationReview.medicationEffectiveness / 10,
        workPerformance: formData.lifeImpact.workImpact / 10,
        relationshipImpact: formData.lifeImpact.relationships / 10,
        dailyActivities: formData.lifeImpact.dailyActivities / 10,
        biggestChallenges: formData.additionalInfo.biggestChallenges,
        whatHelpsMost: formData.additionalInfo.helpfulStrategies,
        goalsNextMonth: formData.additionalInfo.goals,
        questionsForNurse: formData.additionalInfo.questions
      };

      const response = await monthlyAssessmentAPI.createMonthlyAssessment(assessmentData);
      
      if (response.success) {
        // Clear saved assessment data after successful submission
        clearSavedAssessment(sessionId);
        toast.success('Monthly assessment submitted successfully!');
        navigate(`/monthly-assessment-success`);
      } else {
        throw new Error(response.message || 'Failed to submit assessment');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCoreHealthMetrics = () => (
          <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Core Health Metrics</h2>
      
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How well have you managed your ADHD symptoms this month?
              </label>
                <input
            type="range"
            name="coreHealthMetrics.adhdSymptoms"
            value={formData.coreHealthMetrics.adhdSymptoms}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very poorly</span>
            <span>Very well</span>
              </div>
            </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How would you rate your mood over the past month?
              </label>
              <input
            type="range"
            name="coreHealthMetrics.mood"
            value={formData.coreHealthMetrics.mood}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very low</span>
            <span>Very positive</span>
            </div>
            </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How focused and concentrated have you felt?
              </label>
                    <input
            type="range"
            name="coreHealthMetrics.focus"
            value={formData.coreHealthMetrics.focus}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very distracted</span>
            <span>Very focused</span>
              </div>
            </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How would you rate your sleep quality?
              </label>
                    <input
            type="range"
            name="coreHealthMetrics.sleepQuality"
            value={formData.coreHealthMetrics.sleepQuality}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very poor</span>
            <span>Excellent</span>
                </div>
            </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How has your appetite been?
                </label>
                <input
            type="range"
            name="coreHealthMetrics.appetite"
            value={formData.coreHealthMetrics.appetite}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very poor</span>
            <span>Normal/healthy</span>
              </div>
              </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How productive have you been overall?
                  </label>
                  <input
            type="range"
            name="coreHealthMetrics.productivity"
            value={formData.coreHealthMetrics.productivity}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Not productive</span>
            <span>Very productive</span>
                </div>
              </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How much energy have you had?
                </label>
                  <input
            type="range"
            name="coreHealthMetrics.energy"
            value={formData.coreHealthMetrics.energy}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very low energy</span>
            <span>Very high energy</span>
                </div>
              </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How stressed have you felt overall?
                </label>
                      <input
            type="range"
            name="coreHealthMetrics.stress"
            value={formData.coreHealthMetrics.stress}
            onChange={handleSliderChange}
            min="0"
            max="100"
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600">
            <span>Very low stress</span>
            <span>Very high stress</span>
                  </div>
                </div>
              </div>
            </div>
  );

  const renderMedicationReview = () => (
    <div className="space-y-6 bg-white p-6 rounded-md shadow-md">
      <h2 className="text-xl font-bold mb-4">Medication Review</h2>

      {/* Question: Taken as prescribed */}
      <div>
        <p className="font-medium mb-2">Have you taken your medication as prescribed?</p>
        <div className="flex space-x-6">
          {['Yes', 'Sometimes', 'No'].map(option => (
            <label key={option} className="inline-flex items-center space-x-2">
              <input
                type="radio"
                name="medicationReview.takingAsPrescibed"
                value={option}
                checked={formData.medicationReview.takingAsPrescibed === option}
                onChange={handleInputChange}
                className="form-radio text-blue-600"
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Question: Missed doses */}
      <div>
        <p className="font-medium mb-2">On average, how many doses have you missed per week?</p>
        <select
          name="medicationReview.missedDoses"
          value={formData.medicationReview.missedDoses}
          onChange={handleInputChange}
          className="w-full p-2 border rounded-md"
        >
          {Array.from({ length: 8 }, (_, i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
      </div>

      {/* Question: Side effects severity */}
      <div>
        <p className="font-medium mb-2">How severe have your medication side effects been?</p>
        <input
          type="range"
          name="medicationReview.sideEffectsSeverity"
          value={formData.medicationReview.sideEffectsSeverity}
          onChange={handleSliderChange}
          min="0"
          max="100"
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-500">
          <span>None</span>
          <span>Very severe</span>
        </div>
      </div>

      {/* Question: Side effects description */}
      <div>
        <p className="font-medium mb-2">Please describe any side effects you experienced.</p>
        <textarea
          name="medicationReview.sideEffectsDescription"
          value={formData.medicationReview.sideEffectsDescription}
          onChange={handleInputChange}
          rows="3"
          className="w-full p-2 border rounded-md"
          placeholder="Describe any side effects you’ve experienced..."
        />
      </div>

      {/* Question: Medication effectiveness */}
      <div>
        <p className="font-medium mb-2">How effective has your medication been overall?</p>
        <input
          type="range"
          name="medicationReview.medicationEffectiveness"
          value={formData.medicationReview.medicationEffectiveness}
          onChange={handleSliderChange}
          min="0"
          max="100"
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-500">
          <span>Not effective</span>
          <span>Very effective</span>
        </div>
      </div>
    </div>
  );

  const renderLifeImpact = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Life Impact</h2>
      
      <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
          How has ADHD affected your work or career performance?
                </label>
                  <input
                    type="range"
          name="lifeImpact.workImpact"
          value={formData.lifeImpact.workImpact}
          onChange={handleSliderChange}
                    min="0"
                    max="100"
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-600">
          <span>Very negatively</span>
          <span>Not at all</span>
                </div>
              </div>

      <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
          How has ADHD impacted your relationships?
                </label>
                  <input
                    type="range"
          name="lifeImpact.relationships"
          value={formData.lifeImpact.relationships}
          onChange={handleSliderChange}
                    min="0"
                    max="100"
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-600">
          <span>Very negatively</span>
          <span>Positively</span>
                </div>
              </div>

      <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
          How well have you managed your day-to-day activities?
                </label>
                  <input
                    type="range"
          name="lifeImpact.dailyActivities"
          value={formData.lifeImpact.dailyActivities}
          onChange={handleSliderChange}
                    min="0"
                    max="100"
          className="w-full"
        />
        <div className="flex justify-between text-sm text-gray-600">
          <span>Very poorly</span>
          <span>Very well</span>
                  </div>
                </div>
              </div>
  );

  const renderAdditionalInfo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Additional Information</h2>
      
      <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
          What have been your biggest challenges this month?
              </label>
              <textarea
          name="additionalInfo.biggestChallenges"
          value={formData.additionalInfo.biggestChallenges}
                onChange={handleInputChange}
          rows="3"
          className="w-full p-2 border rounded-md"
          placeholder="Describe your biggest challenges..."
              />
            </div>

      <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
          What strategies or treatments helped you most?
              </label>
        <textarea
          name="additionalInfo.helpfulStrategies"
          value={formData.additionalInfo.helpfulStrategies}
                    onChange={handleInputChange}
          rows="3"
          className="w-full p-2 border rounded-md"
          placeholder="Share what worked for you..."
        />
            </div>

      <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
          What are your goals for the upcoming month?
              </label>
        <textarea
          name="additionalInfo.goals"
          value={formData.additionalInfo.goals}
          onChange={handleInputChange}
          rows="3"
          className="w-full p-2 border rounded-md"
          placeholder="Set your goals..."
        />
            </div>

      <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
          Do you have any questions you'd like to ask your nurse?
              </label>
        <textarea
          name="additionalInfo.questions"
          value={formData.additionalInfo.questions}
                  onChange={handleInputChange}
          rows="3"
          className="w-full p-2 border rounded-md"
          placeholder="Ask your questions..."
        />
            </div>
          </div>
        );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCoreHealthMetrics();
      case 2:
        return renderMedicationReview();
      case 3:
        return renderLifeImpact();
      case 4:
        return renderAdditionalInfo();
      default:
        return null;
    }
  };

  // Add session information to the header section
  const renderHeader = () => (
    <div className="mb-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Monthly Assessment Form</h1>
      <p className="text-gray-600">Please complete this form 24 hours before your regular monthly check-in</p>
      <p className="text-gray-600">Your responses help us provide better personalized care</p>
      
      {sessionState && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <div className="text-sm text-gray-600">
            <p><span className="font-medium">Session Date:</span> {new Date(sessionState.sessionDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            <p><span className="font-medium">Session Time:</span> {sessionState.sessionTime}</p>
            <p><span className="font-medium">Nurse:</span> {sessionState.nurseName}</p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderHeader()}
        
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index + 1 === currentStep ? 'bg-blue-600 text-white' : 
                    index + 1 < currentStep ? 'bg-blue-500 text-white' : 'bg-gray-200'
                    }`}>
                      {index + 1 < currentStep ? '✓' : index + 1}
                  </div>
                    <span className="text-xs mt-2">{step.label}</span>
                </div>
                {index < steps.length - 1 && (
                    <div className="flex-1 h-1 mx-2 bg-gray-200">
                      <div className={`h-full bg-blue-600 transition-all ${
                        index + 1 < currentStep ? 'w-full' : 'w-0'
                      }`} />
                    </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
          <form onSubmit={handleSubmit} className="space-y-8">
          {renderStepContent()}

        {/* Navigation Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-center">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                >
                  Previous
                </button>
              ) : (
                <div /> // Keeps spacing when no Previous button
              )}

              {currentStep < steps.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2 ${
                    isSubmitting 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : 'Submit Assessment'}
                </button>
              )}
            </div>


       
          </form>
      </div>
          </div>
    </div>
  );
};

export default MonthlyAssessment; 